// Centrum powiadomien -- dzwonek w naglowku z licznikiem nieprzeczytanych i
// lista: alerty DX, ostrzezenia (burza, wiatr, rozblysk...), potwierdzenia
// QSL, przypomnienia o zawodach, odznaki. Dane: /api/notifications (backend
// zapisuje tam wszystko, co idzie pushem, plus zdarzenia bez pusha).
// Nowa odznaka -> dodatkowo gratulacje w toascie.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Icon from './icons';
import { tx, getLang } from './i18n';

const API = process.env.REACT_APP_API_URL || '';
const POLL_MS = 60 * 1000;
const KIND_ICON = {
  alert: 'bell', storm: 'zap', wind: 'activity', ice: 'alert', flare: 'sun', geomagnetic: 'globe',
  qsl: 'mail', contest: 'flag', badge: 'trophy', invite: 'users',
};
const SEEN_BADGES_KEY = 'hamlogger_badges_toasted';

function timeAgo(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return tx('przed chwilą');
  if (s < 3600) return tx('{0} min temu', [Math.floor(s / 60)]);
  if (s < 86400) return tx('{0} godz. temu', [Math.floor(s / 3600)]);
  return d.toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'pl-PL', { day: 'numeric', month: 'short' });
}

export default function NotificationBell({ onNavigate, notify }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  const toastNewBadges = useCallback((list) => {
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(SEEN_BADGES_KEY) || '[]'); } catch { /* brak */ }
    const fresh = list.filter(n => n.kind === 'badge' && !n.read && !seen.includes(n.id));
    if (!fresh.length) return;
    fresh.slice(0, 2).forEach(n => notify?.(n.title, 'success'));
    try { localStorage.setItem(SEEN_BADGES_KEY, JSON.stringify([...seen, ...fresh.map(n => n.id)].slice(-100))); } catch { /* brak */ }
  }, [notify]);

  const loadList = useCallback(() => {
    axios.get(`${API}/api/notifications`, { params: { limit: 50 } }).then(r => {
      const list = Array.isArray(r.data?.items) ? r.data.items : [];
      setItems(list);
      setUnread(r.data?.unread || 0);
      toastNewBadges(list);
    }).catch(() => setItems(i => i || []));
  }, [toastNewBadges]);

  const poll = useCallback(() => {
    axios.get(`${API}/api/notifications/unread-count`).then(r => {
      const n = r.data?.unread || 0;
      setUnread(prev => { if (n > prev) loadList(); return n; });
    }).catch(() => {});
  }, [loadList]);

  useEffect(() => {
    loadList();
    const t = setInterval(poll, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('hamlogger:notifications-refresh', loadList);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('hamlogger:notifications-refresh', loadList);
    };
  }, [loadList, poll]);

  // Klik poza panelem / Esc zamyka
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const markRead = (body) => axios.post(`${API}/api/notifications/read`, body).catch(() => {});

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const openItem = (n) => {
    if (!n.read) {
      markRead({ ids: [n.id] });
      setItems(list => list.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    if (n.view) { setOpen(false); onNavigate?.(n.view); }
  };

  const readAll = () => {
    markRead({ all: true });
    setItems(list => (list || []).map(x => ({ ...x, read: true })));
    setUnread(0);
  };

  const clearAll = () => {
    if (!window.confirm(tx('Usunąć całą historię powiadomień?'))) return;
    axios.delete(`${API}/api/notifications`).then(() => { setItems([]); setUnread(0); }).catch(() => {});
  };

  const badge = unread > 99 ? '99+' : unread;

  return (
    <div className="nbell" ref={boxRef}>
      <button type="button" className={'nbell__btn' + (open ? ' nbell__btn--open' : '')} onClick={toggle}
        aria-haspopup="dialog" aria-expanded={open}
        title={unread ? tx('Powiadomienia ({0} nieprzeczytanych)', [unread]) : tx('Powiadomienia')}
        aria-label={unread ? tx('Powiadomienia ({0} nieprzeczytanych)', [unread]) : tx('Powiadomienia')}>
        <Icon name="bell" size={17} />
        {unread > 0 && <span className="nbell__count">{badge}</span>}
      </button>
      {open && (
        <div className="nbell__panel" role="dialog" aria-label={tx('Powiadomienia')}>
          <div className="nbell__head">
            <strong>{tx('Powiadomienia')}</strong>
            <span className="nbell__spacer" />
            {unread > 0 && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={readAll}>
                <Icon name="check" size={15} /> {tx('Oznacz jako przeczytane')}
              </button>
            )}
          </div>
          <div className="nbell__list">
            {items === null ? <div className="nbell__empty">{tx('Wczytuję…')}</div>
              : items.length === 0 ? (
                <div className="nbell__empty">
                  <Icon name="bell" size={26} />
                  <p>{tx('Tu pojawią się alerty DX, ostrzeżenia pogodowe, nowe potwierdzenia QSL, przypomnienia o zawodach i zdobyte odznaki.')}</p>
                </div>
              ) : items.map(n => (
                <button key={n.id} type="button" className={'nbell__item' + (n.read ? '' : ' nbell__item--unread') + (n.view ? ' nbell__item--link' : '')}
                  onClick={() => openItem(n)}>
                  <span className={'nbell__icon nbell__icon--' + (n.kind || 'alert')}><Icon name={KIND_ICON[n.kind] || 'bell'} size={16} /></span>
                  <span className="nbell__text">
                    <span className="nbell__title">{n.title}</span>
                    {n.body && <span className="nbell__body">{n.body}</span>}
                    <span className="nbell__time">{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="nbell__dot" aria-label={tx('nieprzeczytane')} />}
                </button>
              ))}
          </div>
          <div className="nbell__foot">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent('hamlogger:account', { detail: 'powiadomienia' })); }}>
              <Icon name="settings" size={15} /> {tx('Ustawienia powiadomień')}
            </button>
            {items?.length > 0 && (
              <button type="button" className="btn btn--ghost btn--sm acc-danger-ghost" onClick={clearAll}>
                <Icon name="trash" size={15} /> {tx('Wyczyść')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
